import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { resetPassword } from "../services/forgotpasswordApi";
import { AuthLayout } from "../components/layout/AuthLayout";

function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();

  const email = location.state?.email || "";
  const otp = location.state?.otp || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const payload = {
      emailAddress: email,
      updatePassword: newPassword,
      otp,
    };

    setLoading(true);
    try {
      const response = await resetPassword(payload);

      if (response.succeeded) {
        alert("Password reset successfully.");
        navigate("/signin");
      } else {
        setError(response.message || "An error occurred. Please try again.");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={
        <>
          Reset <span className="auth-title__accent">password</span>
        </>
      }
      subtitle="Change your password for the existing account."
    >
      {error ? (
        <div className="auth-alert auth-alert--error" role="alert">
          {error}
        </div>
      ) : null}
      {message ? (
        <div className="auth-alert auth-alert--success" role="alert">
          {message}
        </div>
      ) : null}

      <form className="auth-form" onSubmit={handleSubmit}>
        <div className="auth-field auth-password-wrap">
          <label htmlFor="new-password" className="auth-field__label">
            New Password
            <span className="req">*</span>
          </label>
          <input
            type={showPassword ? "text" : "password"}
            id="new-password"
            className="auth-field__input"
            placeholder="Enter your new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
          <button
            type="button"
            className="auth-password-toggle"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff /> : <Eye />}
          </button>
        </div>

        <div className="auth-field auth-password-wrap">
          <label htmlFor="confirm-password" className="auth-field__label">
            Confirm Password
            <span className="req">*</span>
          </label>
          <input
            type={showConfirmPassword ? "text" : "password"}
            id="confirm-password"
            className="auth-field__input"
            placeholder="Confirm your new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
          <button
            type="button"
            className="auth-password-toggle"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
          >
            {showConfirmPassword ? <EyeOff /> : <Eye />}
          </button>
        </div>

        <button
          type="submit"
          className={`auth-submit${loading ? " auth-submit--loading" : ""}`}
          disabled={loading}
        >
          {loading ? "Resetting…" : "Reset Password"}
        </button>
      </form>

      <p className="auth-footer-text">
        <Link to="/signin" className="auth-link">
          Back to sign in
        </Link>
      </p>
    </AuthLayout>
  );
}

export default ResetPassword;
